/**
 * changeTarget — pure decisions behind "the changes are ready, show them".
 *
 * `pickDefaultFile` says which file of a work item's R4 body the panel should land on; and
 * `shouldAutoOpen` says whether a task snapshot entering `changeset_proposed` should open the
 * right-hand panel by itself. Both are pure so the table of cases is testable without React.
 *
 * Nothing here keys off apply/deploy state or wording: the trigger is the approve-changeset card
 * being raised, i.e. an assembled package on a task in `changeset_proposed`.
 */
import type { ItemBody, TaskSnapshot } from "@/services/multi-agent/laneV2/types";
import { isSidecarPath, itemFileStatus } from "./ItemDiffView";
import { buildItemsByKey, matchMemberToWorkItem } from "./views";

type ItemFile = ItemBody["files"][number];

/**
 * The file to land on: the first non-sidecar file that actually changed and has content; else the
 * first changed file of any kind (a sidecar-only edit); else the first non-sidecar with content;
 * else the first with content. `null` when nothing is viewable (delete, no files, no bodies)
 * — the caller then shows the file list.
 */
export function pickDefaultFile(body: ItemBody): string | null {
  if (body.op === "delete") return null;
  const viewable = body.files.filter((f) => f.body !== null);
  const changed = (f: ItemFile) => itemFileStatus(body.op, f, body.truncated) !== "unchanged";
  const primary = viewable.filter((f) => !isSidecarPath(f.path));
  // Changed source, then any changed file (a sidecar-only edit), then the first with content.
  const pick = primary.find(changed) ?? viewable.find(changed) ?? primary[0] ?? viewable[0];
  return pick?.path ?? null;
}

/**
 * A revert task is opened directly in `changeset_proposed` with an inverse package and NO plan and
 * NO build (`core/revert.py` `open_revert_task`; the package is "build-less"). R3 has no explicit
 * revert field, so this structural signal is the safest one: every build task reaches
 * `changeset_proposed` through a plan and a build. Never derived from text.
 */
export function isRevertShaped(snapshot: Pick<TaskSnapshot, "plan" | "build">): boolean {
  return snapshot.plan === null && snapshot.build === null;
}

/** `task_id:package_hash` while the snapshot is a build task's raised approve-changeset card, else null. */
export function changesetKey(snapshot: TaskSnapshot): string | null {
  if (snapshot.state !== "changeset_proposed" || !snapshot.package) return null;
  if (isRevertShaped(snapshot)) return null;
  return `${snapshot.task_id}:${snapshot.package.package_hash}`;
}

export interface AutoOpenContext {
  /** Keys already seen (opened, suppressed, or present when the session was attached). */
  seen: ReadonlySet<string>;
  /** The live stream is connected; anything arriving while attaching/reconnecting is not "new". */
  connected: boolean;
  /** Viewport >= 1280px (`useLaneV2AutoOpen`'s `WIDE_QUERY`): the panel is a column, not a modal Sheet. */
  wide: boolean;
  /** Any workspace section is open. */
  panelOpen: boolean;
  /** `document.visibilityState === "visible"`. */
  visible: boolean;
}

/**
 * The key to open for, or `null`. Fires on the edge only: `next` has a key `prev` did not, and the
 * key was never seen. Whether to open or merely remember is the caller's: it adds the key to `seen`
 * whenever it is returned or when it should be absorbed without opening.
 */
export function shouldAutoOpen(prev: TaskSnapshot | undefined, next: TaskSnapshot, ctx: AutoOpenContext): string | null {
  const key = changesetKey(next);
  if (!key || ctx.seen.has(key)) return null;
  if (prev && changesetKey(prev) === key) return null;
  if (!ctx.connected || !ctx.wide || ctx.panelOpen || !ctx.visible) return null;
  return key;
}

/** Every package member that has a diff to open (changed or not), in package order: what prev/next walks. */
export function viewableItems(snapshot: Pick<TaskSnapshot, "package" | "build">): ChangedItem[] {
  return bodyItems(snapshot).map(({ workItemId, metadataType, fullName }) => ({ workItemId, metadataType, fullName }));
}

function bodyItems(snapshot: Pick<TaskSnapshot, "package" | "build">): Array<ChangedItem & { changed: boolean }> {
  if (!snapshot.package || !snapshot.build) return [];
  const byKey = buildItemsByKey(snapshot.build.items);
  const all: Array<ChangedItem & { changed: boolean }> = [];
  for (const m of snapshot.package.members) {
    const item = matchMemberToWorkItem(m, byKey);
    if (!item?.has_body) continue;
    all.push({ workItemId: item.work_item_id, metadataType: m.metadata_type, fullName: m.full_name, changed: m.body_changed !== false });
  }
  return all;
}

/** One viewable changed component of a package, in package order. */
export interface ChangedItem {
  workItemId: string;
  metadataType: string;
  fullName: string;
}

/**
 * The package's components that have a diff to look at, in package order. A member the wire marks
 * `body_changed === false` (re-saved identical bytes) is left out when anything else changed; if NOTHING
 * changed they are all listed, so the list is never empty just because the diff is empty.
 */
export function changedItems(snapshot: Pick<TaskSnapshot, "package" | "build">): ChangedItem[] {
  const all = bodyItems(snapshot);
  const changed = all.filter((x) => x.changed);
  return (changed.length > 0 ? changed : all).map(({ workItemId, metadataType, fullName }) => ({ workItemId, metadataType, fullName }));
}

/** The work item the panel should land on when it opens by itself: the first changed diff; null → the task overview. */
export function firstChangedWorkItemId(snapshot: Pick<TaskSnapshot, "package" | "build">): string | null {
  return changedItems(snapshot)[0]?.workItemId ?? null;
}
